import { useCallback, useEffect, useRef } from "react"

import { differenceInMinutes, startOfDay } from "date-fns"

import { useScrollbarGutter } from "@/hooks/useScrollbarGutter"
import {
  ROW_HEIGHT,
  TIMELINE_START_HOUR,
  TOTAL_HOURS,
  timeToPosition,
} from "@/lib/timelineScale"

export function useTimelineScroll(selectedDate: Date, hourWidth: number) {
  const timelineHeaderRef = useRef<HTMLDivElement>(null)
  const timelineContentRef = useRef<HTMLDivElement>(null)
  const routeListRef = useRef<HTMLDivElement>(null)
  const isScrollingSyncRef = useRef(false)

  // Labels and header follow the tracks' scroll, so they must reserve the same scrollbar room. See
  // @/lib/scrollbarGutter.
  const scrollbarGutter = useScrollbarGutter(timelineContentRef)

  useEffect(() => {
    if (timelineContentRef.current) {
      const now = new Date()
      const startOfTimeline = startOfDay(selectedDate)
      startOfTimeline.setHours(TIMELINE_START_HOUR)
      const minutesSinceStart = differenceInMinutes(now, startOfTimeline)
      const hoursSinceStart = minutesSinceStart / 60
      // Timeline range is 0-31 hours.
      if (hoursSinceStart >= 0 && hoursSinceStart <= TOTAL_HOURS) {
        const scrollPosition = hoursSinceStart * hourWidth - 200
        timelineContentRef.current.scrollLeft = Math.max(0, scrollPosition)
      }
    }
  }, [hourWidth, selectedDate])

  const handleTimelineScroll = () => {
    if (isScrollingSyncRef.current) return
    isScrollingSyncRef.current = true

    if (timelineHeaderRef.current && timelineContentRef.current) {
      timelineHeaderRef.current.scrollLeft = timelineContentRef.current.scrollLeft
    }
    if (routeListRef.current && timelineContentRef.current) {
      routeListRef.current.scrollTop = timelineContentRef.current.scrollTop
    }

    requestAnimationFrame(() => {
      isScrollingSyncRef.current = false
    })
  }

  /** Row to the top, time a third of the way across. -1 does nothing. */
  const scrollToRow = useCallback(
    (rowIndex: number, time: Date) => {
      if (rowIndex !== -1 && timelineContentRef.current && routeListRef.current) {
        const verticalScrollPosition = rowIndex * ROW_HEIGHT
        timelineContentRef.current.scrollTop = verticalScrollPosition
        routeListRef.current.scrollTop = verticalScrollPosition

        const position = timeToPosition(time, selectedDate, hourWidth)
        const containerWidth = timelineContentRef.current.clientWidth
        const horizontalScrollPosition = Math.max(0, position - containerWidth / 3)
        timelineContentRef.current.scrollLeft = horizontalScrollPosition

        if (timelineHeaderRef.current) {
          timelineHeaderRef.current.scrollLeft = horizontalScrollPosition
        }
      }
    },
    [selectedDate, hourWidth]
  )

  return {
    timelineHeaderRef,
    timelineContentRef,
    routeListRef,
    scrollbarGutter,
    handleTimelineScroll,
    scrollToRow,
  }
}
